import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  limit,
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import Modal from '../components/Modal';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
// Modal removed - too intrusive for new orders
import CategoryManager from '../components/admin/CategoryManager';
import SubcategoryManager from '../components/admin/SubcategoryManager';
import { uploadImage } from '../utils/imageUpload';
import './AdminPage.css';

const badgeOptions = ['', 'New', 'Sale', 'Popular', 'Limited', 'Exclusive', 'Best Seller'];

const availableSizes = ['XS', 'S', 'M', 'L', 'XL'];

const getInitialForm = () => ({
  name: '',
  price: '',
  badge: '',
  category: '',
  subcategory: '',
  image: '',
  image2: '',
  image3: '',
  image4: '',
  description: '',
  selectedSizes: [],
  availability: 'In Stock',
  status: 'draft',
  featured: false,
  editingProductId: null, // Track if we're editing
});

function AdminPage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { notify, notifyCustom, ToastContainer, requestPermission } = useAdminNotifications();
  // Modal removed - users can click on orders to view details instead
  const [uploadingImages, setUploadingImages] = useState({}); // Track upload progress for each image slot
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(getInitialForm());
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('');
  const [productSubcategoryFilter, setProductSubcategoryFilter] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [homepageContent, setHomepageContent] = useState({
    heroEyebrow: 'Cassette 2025 Capsule',
    heroTitle: 'THE TAG Atelier',
    heroCopy: 'Fluid silhouettes, sheer layers, and enduring craftsmanship. Discover abayas designed to feel as weightless as the desert air.',
    heroButton1: 'Shop Abayas',
    heroImages: [
      'https://images.unsplash.com/photo-1583391265014-e66c3a7e92b6?w=1920&q=80',
      'https://images.unsplash.com/photo-1581338834647-b0fb40704e21?w=1920&q=80',
      'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=1920&q=80'
    ],
    aboutEyebrow: 'Who we are',
    aboutTitle: 'Our story',
    aboutParagraph1: 'THE TAG began in 2017 with a desire to create refined abayas that feel effortless. We blend understated luxury with precise tailoring, resulting in silhouettes that move with quiet confidence.',
    aboutParagraph2: 'Every garment is touched by artisans who understand proportion, drape, and the importance of subtle detail. We design limited capsules to ensure attention to every stitch and to keep our collections intentionally curated.',
    aboutButton: 'Learn more',
    aboutImage: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=800&q=80',
    trendyTitle: 'TRENDY ITEMS',
    trendySubtitle: 'DISCOVER OUR LATEST COLLECTION OF PREMIUM ABAYAS',
    shopHeroImage: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=1800&q=80',
  });
  const [isLoadingHomepage, setIsLoadingHomepage] = useState(true);
  const [isSavingHomepage, setIsSavingHomepage] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [previousOrderIds, setPreviousOrderIds] = useState(new Set());
  const isOrdersListenerInitializedRef = useRef(false);
  const [productViewCounts, setProductViewCounts] = useState({});
  const [isLoadingProductViews, setIsLoadingProductViews] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [isLoadingShippingMethods, setIsLoadingShippingMethods] = useState(false);
  const [newShippingMethod, setNewShippingMethod] = useState({ name: '', fee: '', description: '' });
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    category: '',
    subcategory: '',
    badge: '',
    availability: '',
    status: '',
    featured: null, // null = don't change, true/false = set value
  });

  // Accept functionality removed - all orders are visible immediately
  const [settings, setSettings] = useState({
    whatsappMessage: `Hello {customerName}! 👋\n\nThank you for your order {orderId}.\n\nOrder Total: {total} ر.ق\n\nWe'll keep you updated on your order status. If you have any questions, feel free to ask!`,
    contactPhone: '+974 1234 5678',
    contactEmail: 'info@thetag.com',
    contactLocation: 'Doha, Qatar',
    instagramUrl: '',
    // Allow null/0; do not force default here
    deliveryFee: null,
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    featured: false,
    about: false,
    trendy: false,
  });
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });

  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const productsRef = collection(db, 'products');
      let snapshot;
      try {
        const q = query(productsRef, orderBy('createdAt', 'desc'));
        snapshot = await getDocs(q);
      } catch (orderError) {
        snapshot = await getDocs(productsRef);
      }
      
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      items.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.seconds - a.createdAt.seconds;
        }
        return 0;
      });
      
      setProducts(items);
    } catch (error) {
      console.error('Failed to fetch products', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchHomepageContent = useCallback(async () => {
    setIsLoadingHomepage(true);
    // Add a safeguard timeout to ensure loading state always clears
    const timeoutId = setTimeout(() => {
      console.warn('Homepage content fetch taking too long, clearing loading state');
      setIsLoadingHomepage(false);
    }, 10000); // 10 second timeout

    try {
      const homepageRef = doc(db, 'homepage', 'content');
      const docSnap = await getDoc(homepageRef);
      
      clearTimeout(timeoutId); // Clear timeout if fetch succeeds
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setHomepageContent(prev => ({
          ...prev,
          ...data
        }));
      }
      // If document doesn't exist, keep default values
    } catch (error) {
      clearTimeout(timeoutId); // Clear timeout on error
      console.error('Failed to fetch homepage content', error);
      // On error, keep default values and continue
    } finally {
      // Always clear loading state, even if there's an error
      setIsLoadingHomepage(false);
    }
  }, []);

  // Fetch categories from Firestore
  const fetchCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const categoriesRef = collection(db, 'categories');
      const q = query(categoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // If categories collection doesn't exist or has no data, use empty array
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  // Fetch subcategories from Firestore
  const fetchSubcategories = useCallback(async () => {
    setIsLoadingSubcategories(true);
    try {
      const subcategoriesRef = collection(db, 'subcategories');
      const q = query(subcategoriesRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const subcategoriesData = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    } finally {
      setIsLoadingSubcategories(false);
    }
  }, []);

  // Fetch shipping methods from Firestore
  const fetchShippingMethods = useCallback(async () => {
    setIsLoadingShippingMethods(true);
    try {
      const shippingMethodsRef = collection(db, 'shippingMethods');
      const q = query(shippingMethodsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      const shippingMethodsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShippingMethods(shippingMethodsData);
    } catch (error) {
      console.error('Error fetching shipping methods:', error);
      setShippingMethods([]);
    } finally {
      setIsLoadingShippingMethods(false);
    }
  }, []);

  useEffect(() => {
    fetchShippingMethods();
  }, [fetchShippingMethods]);

  // Generate PDF Receipt
  const generateReceipt = useCallback((order) => {
    const pdfDoc = new jsPDF();
    const pageWidth = pdfDoc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Header - Black bar at top
    pdfDoc.setFillColor(0, 0, 0);
    pdfDoc.rect(0, 0, pageWidth, 20, 'F');
    pdfDoc.setTextColor(255, 255, 255);
    pdfDoc.setFontSize(16);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Receipt', pageWidth - margin, 12, { align: 'right' });
    
    yPos = 30;
    pdfDoc.setTextColor(0, 0, 0);

    // Company Name
    pdfDoc.setFontSize(12);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('THE TAG', margin, yPos);
    yPos += 6;

    // Billing Details Box
    pdfDoc.setFillColor(245, 245, 245);
    pdfDoc.rect(margin, yPos, pageWidth - (margin * 2), 35, 'F');
    
    const boxY = yPos + 5;
    let boxYPos = boxY;
    
    // Bill to section
    pdfDoc.setFontSize(9);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Bill to:', margin + 5, boxYPos);
    boxYPos += 5;
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(`${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`, margin + 5, boxYPos);
    boxYPos += 4;
    
    // Address
    const addressParts = [];
    if (order.shipping?.buildingNumber) addressParts.push(`Building ${order.shipping.buildingNumber}`);
    if (order.shipping?.streetNumber) addressParts.push(`Street ${order.shipping.streetNumber}`);
    if (order.shipping?.zoneNumber) addressParts.push(`Zone ${order.shipping.zoneNumber}`);
    if (order.shipping?.city) addressParts.push(order.shipping.city);
    if (order.shipping?.postalCode) addressParts.push(order.shipping.postalCode);
    
    if (addressParts.length === 0 && order.shipping?.address) {
      addressParts.push(order.shipping.address);
      if (order.shipping?.city) addressParts.push(order.shipping.city);
    }
    
    const addressLines = pdfDoc.splitTextToSize(addressParts.join(', ') || 'N/A', 70);
    addressLines.forEach(line => {
      pdfDoc.text(line, margin + 5, boxYPos);
      boxYPos += 4;
    });
    
    if (order.shipping?.country) {
      pdfDoc.text(order.shipping.country, margin + 5, boxYPos);
    }
    
    // Details section (right side)
    const detailsX = pageWidth - margin - 50;
    boxYPos = boxY;
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Details:', detailsX, boxYPos);
    boxYPos += 5;
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text(`Receipt No: ${order.orderId}`, detailsX, boxYPos);
    boxYPos += 4;
    const receiptDate = order.createdAt?.toDate 
      ? new Date(order.createdAt.toDate()).toLocaleDateString() 
      : 'N/A';
    pdfDoc.text(`Receipt Date: ${receiptDate}`, detailsX, boxYPos);
    boxYPos += 4;
    const paymentMethod = order.paymentMethod === 'cash' ? 'Cash on Delivery' : 
                          order.paymentMethod === 'card' ? 'Card Payment' : 
                          order.paymentMethod === 'manual_link' ? 'Manual Link' :
                          order.paymentMethod || 'N/A';
    pdfDoc.text(`Payment: ${paymentMethod}`, detailsX, boxYPos);
    
    yPos += 40;

    // Items Table Header
    pdfDoc.setDrawColor(0, 0, 0);
    pdfDoc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;
    
    pdfDoc.setFontSize(8);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Description', margin, yPos);
    pdfDoc.text('Quantity', margin + 75, yPos);
    pdfDoc.text('Rate', margin + 105, yPos);
    pdfDoc.text('Amount', pageWidth - margin, yPos, { align: 'right' });
    yPos += 5;
    
    pdfDoc.setDrawColor(200, 200, 200);
    pdfDoc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // Order Items
    pdfDoc.setFont('helvetica', 'normal');
    order.items?.forEach((item) => {
      if (yPos > 250) {
        pdfDoc.addPage();
        yPos = margin;
      }
      
      pdfDoc.setFontSize(8);
      const itemName = item.name || 'N/A';
      const itemSpecs = `Size ${item.size || 'N/A'} / Length ${item.length || 'N/A'} / Color ${item.color || 'N/A'}`;
      const fullDescription = `${itemName} (${itemSpecs})`;
      const descLines = pdfDoc.splitTextToSize(fullDescription, 65);
      
      const firstLineY = yPos;
      descLines.forEach((line, idx) => {
        pdfDoc.text(line, margin, yPos);
        if (idx === 0) {
          pdfDoc.text(`${item.quantity || 0}`, margin + 75, firstLineY);
          pdfDoc.text(`${(item.price || 0).toFixed(2)}`, margin + 105, firstLineY);
          const itemTotal = ((item.price || 0) * (item.quantity || 0)).toFixed(2);
          pdfDoc.text(`${itemTotal} QAR`, pageWidth - margin, firstLineY, { align: 'right' });
        }
        yPos += 4;
      });
      
      yPos += 2;
    });

    // Totals Section
    pdfDoc.setDrawColor(200, 200, 200);
    pdfDoc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    pdfDoc.setFontSize(8);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.text('Sub-total', pageWidth - margin - 50, yPos);
    pdfDoc.text(`${(order.totals?.subtotal || 0).toFixed(2)} QAR`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 5;
    
    pdfDoc.text('Delivery', pageWidth - margin - 50, yPos);
    pdfDoc.text(`${(order.totals?.shipping || 0).toFixed(2)} QAR`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;
    
    // Total Paid - bold line
    pdfDoc.setDrawColor(0, 0, 0);
    pdfDoc.setLineWidth(0.5);
    pdfDoc.line(pageWidth - margin - 50, yPos, pageWidth - margin, yPos);
    yPos += 6;
    
    pdfDoc.setFontSize(10);
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('Total Paid', pageWidth - margin - 50, yPos);
    pdfDoc.setFontSize(12);
    pdfDoc.text(`${(order.totals?.total || 0).toFixed(2)} QAR`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;

    // Notes section (if needed)
    if (yPos < 250) {
      pdfDoc.setFontSize(8);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Notes:', margin, yPos);
      yPos += 5;
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.text('Thank you for your purchase!', margin, yPos);
    }

    // Save the PDF
    const fileName = `Receipt-${order.orderId}.pdf`;
    pdfDoc.save(fileName);
  }, []);

  // Request notification permission on mount and initialize audio + service worker
  useEffect(() => {
    // Wrap everything in try-catch to prevent white screen on mobile
    try {
      requestPermission();
    } catch (e) {
      console.warn('Error requesting permission:', e);
    }
    
    // Register service worker for background notifications (non-blocking)
    const initServiceWorker = async () => {
      try {
        const { registerServiceWorker, registerPeriodicSync } = await import('../utils/serviceWorker');
        await registerServiceWorker();
        await registerPeriodicSync();
      } catch (e) {
        console.warn('Service worker initialization failed:', e);
      }
    };
    
    // Don't await - let it run in background
    initServiceWorker().catch(err => {
      console.warn('Service worker init error (non-critical):', err);
    });
    
    // Initialize audio context on first user interaction (required by browsers)
    const initAudio = async () => {
      try {
        const { getAudioContext } = await import('../utils/notifications');
        // Initialize on first click/interaction
        const initOnInteraction = async () => {
          try {
            await getAudioContext();
          } catch (e) {
            console.warn('Audio context init error:', e);
          }
        };
        
        // Try multiple interaction types
        ['click', 'touchstart', 'keydown'].forEach(eventType => {
          document.addEventListener(eventType, initOnInteraction, { once: true });
        });
      } catch (e) {
        console.warn('Audio initialization:', e);
      }
    };
    
    initAudio();
  }, [requestPermission]);

  // Generate a random, unique order number (same logic as Checkout)
  const getNextOrderNumber = async () => {
    try {
      const generateCandidate = () => {
        const digits = '0123456789';
        const length = 8;
        const cryptoObj = typeof window !== 'undefined' ? window.crypto : null;
        if (cryptoObj?.getRandomValues) {
          const values = new Uint32Array(length);
          cryptoObj.getRandomValues(values);
          const randomPart = Array.from(values, value => digits[value % digits.length]).join('');
          return `ORDER-${randomPart}`;
        }
        let randomPart = '';
        for (let i = 0; i < length; i += 1) {
          randomPart += digits[Math.floor(Math.random() * digits.length)];
        }
        return `ORDER-${randomPart}`;
      };

      const isOrderIdTaken = async (candidate) => {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('orderId', '==', candidate),
          limit(1)
        );
        const pendingQuery = query(
          collection(db, 'pendingOrders'),
          where('orderId', '==', candidate),
          limit(1)
        );

        const [ordersSnap, pendingSnap] = await Promise.all([
          getDocs(ordersQuery),
          getDocs(pendingQuery),
        ]);

        return !ordersSnap.empty || !pendingSnap.empty;
      };

      for (let attempt = 0; attempt < 20; attempt += 1) {
        const candidate = generateCandidate();
        const exists = await isOrderIdTaken(candidate);
        if (!exists) {
          return candidate;
        }
      }
    } catch (error) {
      console.error('Error generating order number:', error);
    }

    const fallbackSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `ORDER-${Date.now()}-${fallbackSuffix}`;
  };

  // Set up real-time orders listener
  useEffect(() => {
    setIsLoadingOrders(true);
    const ordersRef = collection(db, 'orders');
    
    // Show all orders (unfiltered). No Firestore orderBy to avoid index requirements; sort client-side.
    const q = ordersRef;

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Show all orders (unfiltered) - but only paid orders are considered "real orders"
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      items.sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.seconds - a.createdAt.seconds;
        }
        return 0;
      });

      // Accept functionality removed - all orders are visible immediately
      
      // Check for new orders (only after listener is initialized) - for sound notifications
      setPreviousOrderIds(prevIds => {
        const currentOrderIds = new Set(items.map(item => item.id));
        
        // Only check for new orders after the listener has been initialized
        // This prevents showing modals for existing orders on first load
        if (isOrdersListenerInitializedRef.current && prevIds.size > 0) {
          // Only treat paid orders as "real orders" - pending orders don't trigger notifications
          const newPaidOrders = items.filter(item => 
            !prevIds.has(item.id) && item.paymentStatus === 'paid'
          );
          
          if (newPaidOrders.length > 0) {
            console.log('New paid orders detected:', newPaidOrders.length);
            // Just show a subtle notification - no sounds, no modal, no browser notifications
            // Simplepush already handles external notifications
            newPaidOrders.forEach((order) => {
              const orderTotal = order.totals?.total || 0;
              const isUrgent = orderTotal > 1000;
              
              // Only add to notification center (subtle badge) - no sounds, no browser notifications
              notifyCustom(
                isUrgent ? 'URGENT: High Value Order' : 'New Order',
                `Order ${order.orderId} - ${orderTotal.toFixed(2)} ر.ق`,
                isUrgent ? 'urgent' : 'info',
                { sound: false, browser: false } // Disable sounds and browser notifications
              );
            });
            
            // Don't show modal automatically - user can click on order to view details
            // Remove modal auto-show
          }
        }
        
        // Mark listener as initialized after first snapshot
        if (!isOrdersListenerInitializedRef.current) {
          isOrdersListenerInitializedRef.current = true;
        }
        
        // Return new set of order IDs
        return new Set(items.map(item => item.id));
      });

      // Don't auto-show modal - let user click on orders to view details
      // This reduces interruptions and makes the experience calmer
      // Simplepush already handles external notifications

      setOrders(items);
      setIsLoadingOrders(false);
    }, (error) => {
      console.error('Failed to listen to orders', error);
      setIsLoadingOrders(false);
    });

    return () => unsubscribe();
  }, [notify, notifyCustom]);

  // Fetch settings from Firestore
  const fetchSettings = useCallback(async () => {
    try {
      const settingsRef = doc(db, 'settings', 'general');
      const settingsDoc = await getDoc(settingsRef);
      if (settingsDoc.exists()) {
        setSettings(prev => ({
          ...prev,
          ...settingsDoc.data()
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  // Save settings to Firestore
  const saveSettings = async (newSettings) => {
    try {
      const settingsRef = doc(db, 'settings', 'general');
      // Normalize deliveryFee to number (allow 0) if provided
      const normalizedSettings = {
        ...newSettings,
        deliveryFee: newSettings.deliveryFee === '' || newSettings.deliveryFee === null || newSettings.deliveryFee === undefined
          ? 0
          : Number(newSettings.deliveryFee),
      };

      await setDoc(settingsRef, {
        ...normalizedSettings,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSettings(normalizedSettings);
      setFeedback({ type: 'success', message: 'Settings saved successfully! Changes have been applied.' });
      // Clear feedback after 3 seconds
      setTimeout(() => {
        setFeedback(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to save settings', error);
      setFeedback({ type: 'error', message: 'Failed to save settings. Please try again.' });
      setTimeout(() => {
        setFeedback(null);
      }, 3000);
    }
  };

  const fetchProductViews = useCallback(async () => {
    setIsLoadingProductViews(true);
    try {
      const viewsRef = collection(db, 'productViews');
      const since = new Date();
      since.setDate(since.getDate() - 30);
      const snapshot = await getDocs(query(viewsRef, where('viewedAt', '>=', since)));

      const counts = {};
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (!data.productId) return;
        counts[data.productId] = (counts[data.productId] || 0) + 1;
      });
      setProductViewCounts(counts);
    } catch (error) {
      console.error('Failed to fetch product views:', error);
      setProductViewCounts({});
    } finally {
      setIsLoadingProductViews(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchHomepageContent();
    fetchSettings();
    fetchCategories();
    fetchSubcategories();
    fetchProductViews();
  }, [fetchProducts, fetchHomepageContent, fetchSettings, fetchCategories, fetchSubcategories, fetchProductViews]);

  const handleHomepageChange = (field, value) => {
    setHomepageContent(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleHeroImageChange = (index, value) => {
    const newImages = [...homepageContent.heroImages];
    newImages[index] = value;
    setHomepageContent(prev => ({
      ...prev,
      heroImages: newImages
    }));
    setHasUnsavedChanges(true);
  };

  const handleHeroImageUpload = async (file, index) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFeedback({ type: 'error', message: 'Please select an image file' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFeedback({ type: 'error', message: 'Image size must be less than 5MB' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    const fieldKey = `heroImage${index}`;
    setUploadingImages(prev => ({ ...prev, [fieldKey]: 0 }));

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to upload images. Please log in again.');
      }

      const downloadURL = await uploadImage(file, 'site', (progress) => {
        setUploadingImages(prev => ({ ...prev, [fieldKey]: Math.min(progress, 100) }));
      });

      const newImages = [...homepageContent.heroImages];
      newImages[index] = downloadURL;
      setHomepageContent(prev => ({
        ...prev,
        heroImages: newImages
      }));
      setHasUnsavedChanges(true);
      setUploadingImages(prev => {
        const nextState = { ...prev };
        delete nextState[fieldKey];
        return nextState;
      });
    } catch (error) {
      console.error('Error uploading hero image:', error);
      const errorMessage = error.message || 'Failed to upload image. Please try again.';
      setFeedback({ type: 'error', message: errorMessage });
      setTimeout(() => setFeedback(null), 5000);
      setUploadingImages(prev => {
        const nextState = { ...prev };
        delete nextState[fieldKey];
        return nextState;
      });
    }
  };

  const handleHomepageImageUpload = async (file, field) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFeedback({ type: 'error', message: 'Please select an image file' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFeedback({ type: 'error', message: 'Image size must be less than 5MB' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    setUploadingImages(prev => ({ ...prev, [field]: 0 }));

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to upload images. Please log in again.');
      }

      const downloadURL = await uploadImage(file, 'site', (progress) => {
        setUploadingImages(prev => ({ ...prev, [field]: Math.min(progress, 100) }));
      });

      setHomepageContent(prev => ({ ...prev, [field]: downloadURL }));
      setHasUnsavedChanges(true);
      setUploadingImages(prev => {
        const nextState = { ...prev };
        delete nextState[field];
        return nextState;
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error.message || 'Failed to upload image. Please try again.';
      setFeedback({ type: 'error', message: errorMessage });
      setTimeout(() => setFeedback(null), 5000);
      setUploadingImages(prev => {
        const nextState = { ...prev };
        delete nextState[field];
        return nextState;
      });
    }
  };

  // Format phone number for WhatsApp (remove spaces, dashes, parentheses, and ensure country code)
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    // If it doesn't start with +, assume it's Qatar (+974) and add it
    if (!cleaned.startsWith('+')) {
      // If it starts with 0, replace with +974
      if (cleaned.startsWith('0')) {
        cleaned = '+974' + cleaned.substring(1);
      } else {
        cleaned = '+974' + cleaned;
      }
    }
    // Remove + for WhatsApp URL (wa.me doesn't need it)
    return cleaned.replace('+', '');
  };

  // Replace placeholders in WhatsApp message template
  const formatWhatsAppMessage = (order) => {
    const orderId = order.orderId || order.id;
    const customerName = `${order.customer?.firstName || ''} ${order.customer?.lastName || ''}`.trim() || 'there';
    const total = order.totals?.total?.toFixed(2) || '0.00';
    const itemCount = order.items?.length || 0;
    const items = order.items?.map(item => `${item.name} (${item.quantity}x)`).join(', ') || '';
    
    let message = settings.whatsappMessage || '';
    message = message.replace(/{customerName}/g, customerName);
    message = message.replace(/{orderId}/g, orderId);
    message = message.replace(/{total}/g, total);
    message = message.replace(/{itemCount}/g, itemCount.toString());
    message = message.replace(/{items}/g, items);
    
    return message;
  };

  // Open WhatsApp with order details
  const openWhatsApp = (order) => {
    const phone = formatPhoneForWhatsApp(order.customer?.phone);
    if (!phone) {
      setFeedback({ type: 'error', message: 'Phone number not available.' });
      return;
    }
    
    // Use customizable message template
    const message = encodeURIComponent(formatWhatsAppMessage(order));
    
    // Open WhatsApp
    const whatsappUrl = `https://wa.me/${phone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const expandAllSections = () => {
    setExpandedSections({
      hero: true,
      featured: true,
      about: true,
      trendy: true,
    });
  };

  const collapseAllSections = () => {
    setExpandedSections({
      hero: false,
      featured: false,
      about: false,
      trendy: false,
    });
  };

  const handleSaveHomepage = async () => {
    setIsSavingHomepage(true);
    try {
      const homepageRef = doc(db, 'homepage', 'content');
      await setDoc(homepageRef, {
        ...homepageContent,
        updatedAt: serverTimestamp(),
      });
      setFeedback({ type: 'success', message: 'Homepage content saved successfully!' });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save homepage content', error);
      setFeedback({ type: 'error', message: 'Failed to save homepage content.' });
    } finally {
      setIsSavingHomepage(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'category') {
      setFormData(prev => ({
        ...prev,
        category: value,
        subcategory: '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    setHasUnsavedChanges(true);
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => ({
      ...prev,
      selectedSizes: prev.selectedSizes.includes(size)
        ? prev.selectedSizes.filter(s => s !== size)
        : [...prev.selectedSizes, size]
    }));
    setHasUnsavedChanges(true);
  };


  // Handle image file upload
  const handleImageUpload = async (file, imageSlot) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFeedback({ type: 'error', message: 'Please select an image file' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFeedback({ type: 'error', message: 'Image size must be less than 5MB' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    // Set uploading state
    setUploadingImages(prev => ({ ...prev, [imageSlot]: 0 }));

    try {
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('You must be logged in to upload images. Please log in again.');
      }

      const downloadURL = await uploadImage(file, 'products', (progress) => {
        setUploadingImages(prev => ({ ...prev, [imageSlot]: Math.min(progress, 100) }));
      });

      // Update form data with the download URL
      setFormData(prev => ({ ...prev, [imageSlot]: downloadURL }));
      setHasUnsavedChanges(true);
      setUploadingImages(prev => {
        const newState = { ...prev };
        delete newState[imageSlot];
        return newState;
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      const errorMessage = error.message || 'Failed to upload image. Please try again.';
      setFeedback({ 
        type: 'error', 
        message: errorMessage
      });
      setTimeout(() => setFeedback(null), 5000);
      setUploadingImages(prev => {
        const newState = { ...prev };
        delete newState[imageSlot];
        return newState;
      });
    }
  };

  const handleDiscard = () => {
    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Discard Changes?',
      message: 'Are you sure you want to discard all changes? This action cannot be undone.',
      onConfirm: () => {
        setFormData(getInitialForm());
        setHasUnsavedChanges(false);
        setModal({ ...modal, isOpen: false });
      },
      showCancel: true,
      confirmText: 'Discard',
      cancelText: 'Cancel'
    });
  };

  // Bulk edit handler
  const handleBulkEdit = async () => {
    if (selectedProducts.size === 0) return;
    
    setIsSaving(true);
    setFeedback(null);

    try {
      const updates = {};
      if (bulkEditData.category) updates.category = bulkEditData.category;
      if (bulkEditData.subcategory === '__clear__') updates.subcategory = null;
      else if (bulkEditData.subcategory) updates.subcategory = bulkEditData.subcategory;
      if (bulkEditData.badge === '__clear__') updates.badge = null;
      else if (bulkEditData.badge) updates.badge = bulkEditData.badge;
      if (bulkEditData.availability) updates.availability = bulkEditData.availability;
      if (bulkEditData.status) updates.status = bulkEditData.status;
      if (bulkEditData.featured !== null) updates.featured = bulkEditData.featured;
      updates.updatedAt = serverTimestamp();

      const updatePromises = Array.from(selectedProducts).map(productId => {
        const productRef = doc(db, 'products', productId);
        return updateDoc(productRef, updates);
      });

      await Promise.all(updatePromises);
      
      setFeedback({ type: 'success', message: `Successfully updated ${selectedProducts.size} product(s)!` });
      setSelectedProducts(new Set());
      setIsBulkEditOpen(false);
      setBulkEditData({
        category: '',
        subcategory: '',
        badge: '',
        availability: '',
        status: '',
        featured: false,
      });
      fetchProducts();
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      console.error('Error bulk editing products:', error);
      setFeedback({ type: 'error', message: 'Failed to update products. Please try again.' });
      setTimeout(() => setFeedback(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  // Image reordering handlers
  const handleImageReorder = (dragIndex, dropIndex) => {
    const allImages = [
      formData.image,
      formData.image2,
      formData.image3,
      formData.image4
    ].filter(img => img && img.trim() !== '');
    
    if (dragIndex === dropIndex || dragIndex >= allImages.length || dropIndex >= allImages.length) {
      return;
    }
    
    const reorderedImages = [...allImages];
    const [draggedImage] = reorderedImages.splice(dragIndex, 1);
    reorderedImages.splice(dropIndex, 0, draggedImage);
    
    setFormData(prev => ({
      ...prev,
      images: reorderedImages,
      image: reorderedImages[0] || '',
      image2: reorderedImages[1] || '',
      image3: reorderedImages[2] || '',
      image4: reorderedImages[3] || '',
    }));
    setHasUnsavedChanges(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFeedback(null);

    try {
      // Build images array with only actual images (no duplicates, no empty strings)
      const allImages = [
          formData.image.trim(),
          formData.image2.trim(),
          formData.image3.trim(),
          formData.image4.trim()
        ].filter(img => img && img.trim() !== ''); // Filter out empty strings
      
      // Remove duplicates
      const imagesArray = [...new Set(allImages)];

      const productData = {
        name: formData.name.trim(),
        price: Number(formData.price),
        badge: formData.badge || null,
        category: formData.category || '',
        subcategory: formData.subcategory || '',
        image: formData.image.trim() || imagesArray[0] || '',
        images: imagesArray, // Only actual images, no duplicates
        description: formData.description.trim(),
        sizes: formData.selectedSizes,
        availability: formData.availability,
        status: formData.status,
        featured: formData.featured || false,
        updatedAt: serverTimestamp(),
      };

      if (formData.editingProductId) {
        // Update existing product
        const productRef = doc(db, 'products', formData.editingProductId);
        await updateDoc(productRef, productData);
        setFeedback({ type: 'success', message: 'Product updated successfully!' });
      } else {
        // Create new product
        productData.createdAt = serverTimestamp();
        const productsRef = collection(db, 'products');
        await addDoc(productsRef, productData);
        setFeedback({ type: 'success', message: 'Product added successfully!' });
      }

      setFormData(getInitialForm());
      setHasUnsavedChanges(false);
      fetchProducts();
      setActiveSection('products');
    } catch (error) {
      console.error('Failed to save product', error);
      setFeedback({ type: 'error', message: 'Failed to save product. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const paidOrders = orders.filter(order => order.paymentStatus === 'paid');
  const totalRevenue = paidOrders.reduce((sum, order) => sum + (order.totals?.total || 0), 0);
  const averageOrderValue = paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0;
  const salesByProduct = useMemo(() => {
    const map = new Map();
    paidOrders.forEach(order => {
      (order.items || []).forEach(item => {
        if (!item?.id) return;
        const existing = map.get(item.id) || {
          productId: item.id,
          name: item.name || '',
          units: 0,
          revenue: 0,
        };
        const quantity = Number(item.quantity) || 0;
        const price = Number(item.price) || 0;
        existing.units += quantity;
        existing.revenue += price * quantity;
        if (!existing.name && item.name) {
          existing.name = item.name;
        }
        map.set(item.id, existing);
      });
    });
    return map;
  }, [paidOrders]);
  const totalUnitsSold = Array.from(salesByProduct.values()).reduce((sum, entry) => sum + entry.units, 0);

  const totalProducts = products.length;
  const featuredCount = products.filter(p => p.featured).length;
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchQuery
      ? (product.name || '').toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    const matchesCategory = productCategoryFilter
      ? product.category === productCategoryFilter
      : true;
    const matchesSubcategory = productSubcategoryFilter
      ? product.subcategory === productSubcategoryFilter
      : true;
    return matchesSearch && matchesCategory && matchesSubcategory;
  });
  const availableSubcategories = useMemo(() => {
    if (!formData.category) return [];
    return subcategories.filter(subcategory => subcategory.categoryId === formData.category);
  }, [formData.category, subcategories]);
  const filterSubcategories = useMemo(() => {
    if (!productCategoryFilter) return [];
    return subcategories.filter(subcategory => subcategory.categoryId === productCategoryFilter);
  }, [productCategoryFilter, subcategories]);

  return (
    <div className="admin-page-shopify">
      <button 
        className="mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        aria-label="Toggle menu"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {mobileMenuOpen ? (
            <path d="M18 6L6 18M6 6l12 12" />
          ) : (
            <path d="M3 12h18M3 6h18M3 18h18" />
          )}
        </svg>
      </button>

      <div className={`admin-layout-shopify ${mobileMenuOpen ? 'menu-open' : ''}`}>
        {/* Left Sidebar */}
        <aside className={`admin-sidebar-shopify ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-brand">THE TAG</div>
          <nav className="admin-nav-shopify">
            <button
              className={`nav-item-shopify ${activeSection === 'home' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('home');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              <span>Home</span>
            </button>
            <div className="nav-group-shopify">
              <button
                className={`nav-item-shopify ${activeSection === 'products' ? 'active' : ''}`}
                onClick={() => {
                  setActiveSection('products');
                  setMobileMenuOpen(false);
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                <span>Products</span>
              </button>
              <button
                className={`nav-item-shopify ${activeSection === 'categories' ? 'active' : ''}`}
                onClick={() => {
                  setActiveSection('categories');
                  setMobileMenuOpen(false);
                  fetchCategories(); // Refresh categories when navigating to this section
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16M4 12h16M4 17h16"></path>
                </svg>
                <span>Categories</span>
              </button>
            </div>
            <button
              className={`nav-item-shopify ${activeSection === 'add' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('add');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              <span>Add Product</span>
            </button>
            <button
              className={`nav-item-shopify ${activeSection === 'reports' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('reports');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="20" x2="18" y2="10"></line>
                <line x1="12" y1="20" x2="12" y2="4"></line>
                <line x1="6" y1="20" x2="6" y2="14"></line>
              </svg>
              <span>Sales Report</span>
            </button>
            <button
              className={`nav-item-shopify ${activeSection === 'homepage' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('homepage');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="3" y1="9" x2="21" y2="9"></line>
                <line x1="9" y1="21" x2="9" y2="9"></line>
              </svg>
              <span>Homepage Content</span>
            </button>
            <button
              className={`nav-item-shopify ${activeSection === 'orders' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('orders');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              <span>Orders</span>
              {orders.filter(o => o.paymentStatus !== 'paid').length > 0 && (
                <span style={{
                  marginLeft: 'auto',
                  background: '#000000',
                  color: '#ffffff',
                  borderRadius: '10px',
                  padding: '2px 6px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {orders.filter(o => o.paymentStatus !== 'paid').length}
                </span>
              )}
            </button>
            <button
              className={`nav-item-shopify ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('settings');
                setMobileMenuOpen(false);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3m15.364 6.364l-4.243-4.243m-4.242 0L5.636 18.364m12.728-12.728l-4.243 4.243m-4.242 0L5.636 5.636"></path>
              </svg>
              <span>Settings</span>
            </button>
            <div className="nav-divider-shopify"></div>
            <button className="nav-item-shopify" onClick={() => navigate('/')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>Back to Site</span>
            </button>
            <button 
              className="nav-item-shopify nav-item-logout" 
              onClick={async () => {
                try {
                  await signOut(auth);
                  localStorage.removeItem('admin_last_activity');
                  localStorage.removeItem('admin_attempts');
                  localStorage.removeItem('admin_lockout');
                  navigate('/admin');
                } catch (error) {
                  console.error('Error signing out:', error);
                  setModal({
                    isOpen: true,
                    type: 'error',
                    title: 'Logout Error',
                    message: 'Failed to sign out. Please try again.',
                    onConfirm: () => setModal({ ...modal, isOpen: false })
                  });
                }
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>Logout</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main-shopify">
          {/* Top Header */}
          <div style={{ 
            padding: '16px 24px', 
            borderBottom: '1px solid #e5e7eb', 
            display: 'flex', 
            justifyContent: 'flex-start',
            alignItems: 'center',
            background: '#ffffff',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
          </div>

          {activeSection === 'home' && (
            <AnalyticsDashboard orders={orders} products={products} />
          )}

          {activeSection === 'homepage' && (
            <div className="admin-homepage-shopify">
              <div className="page-header-shopify">
                <div>
                  <h1>Homepage Content</h1>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    Manage your homepage sections. Click on any section to expand or collapse it.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="btn-secondary-shopify" 
                      onClick={expandAllSections}
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      Expand All
                    </button>
                    <button 
                      className="btn-secondary-shopify" 
                      onClick={collapseAllSections}
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                    >
                      Collapse All
                    </button>
                  </div>
                  {hasUnsavedChanges && (
                    <button className="btn-primary-shopify" onClick={handleSaveHomepage} disabled={isSavingHomepage}>
                      {isSavingHomepage ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                </div>
              </div>

              {feedback && (
                <div className={`feedback ${feedback.type}`}>
                  {feedback.message}
                </div>
              )}

              {isLoadingHomepage ? (
                <p className="admin-placeholder">Loading<span className="loading-dots"></span></p>
              ) : (
                <div className="homepage-form-shopify">
                  {/* Hero Section */}
                  <div className="homepage-section-shopify">
                    <button 
                      className="section-header-toggle-shopify"
                      onClick={() => toggleSection('hero')}
                    >
                      <h2>Hero Section</h2>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className={expandedSections.hero ? 'section-icon-expanded' : ''}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {expandedSections.hero && (
                      <div className="section-content-shopify">
                        <div className="form-group-shopify">
                      <label>Eyebrow Text</label>
                      <input
                        type="text"
                        value={homepageContent.heroEyebrow}
                        onChange={(e) => handleHomepageChange('heroEyebrow', e.target.value)}
                        placeholder="Cassette 2025 Capsule"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Title</label>
                      <input
                        type="text"
                        value={homepageContent.heroTitle}
                        onChange={(e) => handleHomepageChange('heroTitle', e.target.value)}
                        placeholder="THE TAG Atelier"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Description</label>
                      <textarea
                        value={homepageContent.heroCopy}
                        onChange={(e) => handleHomepageChange('heroCopy', e.target.value)}
                        rows="3"
                        placeholder="Fluid silhouettes, sheer layers..."
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group-shopify">
                        <label>Button 1 Text</label>
                        <input
                          type="text"
                          value={homepageContent.heroButton1}
                          onChange={(e) => handleHomepageChange('heroButton1', e.target.value)}
                          placeholder="Shop Abayas"
                        />
                      </div>
                    </div>
                    <div className="form-group-shopify">
                      <label>Hero Images (up to 3)</label>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                        Upload images for the hero slideshow (max 3 images, 5MB each). Supported formats: JPG, PNG, WebP
                      </p>
                      <div className="media-grid-shopify" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
                        {[0, 1, 2].map((index) => {
                          const fieldKey = `heroImage${index}`;
                          const imageUrl = homepageContent.heroImages[index];
                          return (
                            <div key={index} style={{ marginBottom: '1rem' }}>
                              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>
                                Hero Image {index + 1}
                              </label>
                              {imageUrl ? (
                                <div className="media-item-shopify">
                                  <div className="media-thumbnail-shopify" style={{ backgroundImage: `url(${imageUrl})` }} />
                                  <button 
                                    type="button" 
                                    className="media-remove-shopify" 
                                    onClick={() => {
                                      const newImages = [...homepageContent.heroImages];
                                      newImages[index] = '';
                                      setHomepageContent(prev => ({
                                        ...prev,
                                        heroImages: newImages
                                      }));
                                      setHasUnsavedChanges(true);
                                    }}
                                    disabled={uploadingImages[fieldKey] !== undefined}
                                  >
                                    ×
                                  </button>
                                  {uploadingImages[fieldKey] !== undefined && (
                                    <div className="upload-progress-overlay">
                                      <div className="upload-progress-bar" style={{ width: `${uploadingImages[fieldKey]}%` }}></div>
                                      <span className="upload-progress-text">{Math.round(uploadingImages[fieldKey])}%</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="media-item-shopify">
                                  <label className="media-upload-label-shopify">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) handleHeroImageUpload(file, index);
                                        e.target.value = '';
                                      }}
                                      style={{ display: 'none' }}
                                      disabled={uploadingImages[fieldKey] !== undefined}
                                    />
                                    <div className="media-upload-placeholder">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="17 8 12 3 7 8"></polyline>
                                        <line x1="12" y1="3" x2="12" y2="15"></line>
                                      </svg>
                                      <span>Upload Image {index + 1}</span>
                                    </div>
                                  </label>
                                  {uploadingImages[fieldKey] !== undefined && (
                                    <div className="upload-progress-overlay">
                                      <div className="upload-progress-bar" style={{ width: `${uploadingImages[fieldKey]}%` }}></div>
                                      <span className="upload-progress-text">{Math.round(uploadingImages[fieldKey])}%</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="form-group-shopify">
                      <label>Shop Hero Image</label>
                      <div className="media-grid-shopify" style={{ maxWidth: '220px' }}>
                        {homepageContent.shopHeroImage ? (
                          <div className="media-item-shopify">
                            <div className="media-thumbnail-shopify" style={{ backgroundImage: `url(${homepageContent.shopHeroImage})` }} />
                            <button 
                              type="button" 
                              className="media-remove-shopify" 
                              onClick={() => handleHomepageChange('shopHeroImage', '')}
                              disabled={uploadingImages.shopHeroImage !== undefined}
                            >
                              ×
                            </button>
                            {uploadingImages.shopHeroImage !== undefined && (
                              <div className="upload-progress-overlay">
                                <div className="upload-progress-bar" style={{ width: `${uploadingImages.shopHeroImage}%` }}></div>
                                <span className="upload-progress-text">{Math.round(uploadingImages.shopHeroImage)}%</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="media-item-shopify">
                            <label className="media-upload-label-shopify">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleHomepageImageUpload(file, 'shopHeroImage');
                                  e.target.value = '';
                                }}
                                style={{ display: 'none' }}
                                disabled={uploadingImages.shopHeroImage !== undefined}
                              />
                              <div className="media-upload-placeholder">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="17 8 12 3 7 8"></polyline>
                                  <line x1="12" y1="3" x2="12" y2="15"></line>
                                </svg>
                                <span>Upload Shop Hero</span>
                              </div>
                            </label>
                            {uploadingImages.shopHeroImage !== undefined && (
                              <div className="upload-progress-overlay">
                                <div className="upload-progress-bar" style={{ width: `${uploadingImages.shopHeroImage}%` }}></div>
                                <span className="upload-progress-text">{Math.round(uploadingImages.shopHeroImage)}%</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <input
                        type="url"
                        value={homepageContent.shopHeroImage || ''}
                        onChange={(e) => handleHomepageChange('shopHeroImage', e.target.value)}
                        placeholder="https://..."
                        style={{ marginTop: '0.75rem' }}
                      />
                      {homepageContent.shopHeroImage && (
                        <div className="image-preview-shopify" style={{ backgroundImage: `url(${homepageContent.shopHeroImage})` }} />
                      )}
                    </div>
                      </div>
                    )}
                  </div>

                  {/* Featured Products Section */}
                  <div className="homepage-section-shopify">
                    <button 
                      className="section-header-toggle-shopify"
                      onClick={() => toggleSection('featured')}
                    >
                      <h2>Featured Products</h2>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className={expandedSections.featured ? 'section-icon-expanded' : ''}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {expandedSections.featured && (
                      <div className="section-content-shopify">
                        <p className="section-description-shopify">Select which products appear on the homepage. Products marked as "Featured" in the Products page will appear here.</p>
                        <div className="featured-products-list-shopify">
                      {products.filter(p => p.featured).length === 0 ? (
                        <p>No featured products. Go to Products page and mark products as "Featured".</p>
                      ) : (
                        <div className="featured-grid-shopify">
                          {products.filter(p => p.featured).slice(0, 4).map(product => (
                            <div key={product.id} className="featured-item-shopify">
                              <div className="featured-image-shopify" style={{ backgroundImage: `url(${product.image})` }} />
                              <div className="featured-name-shopify">{product.name}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                      </div>
                    )}
                  </div>

                  {/* About/Story Section */}
                  <div className="homepage-section-shopify">
                    <button 
                      className="section-header-toggle-shopify"
                      onClick={() => toggleSection('about')}
                    >
                      <h2>About / Story Section</h2>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className={expandedSections.about ? 'section-icon-expanded' : ''}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {expandedSections.about && (
                      <div className="section-content-shopify">
                        <div className="form-group-shopify">
                      <label>Eyebrow Text</label>
                      <input
                        type="text"
                        value={homepageContent.aboutEyebrow}
                        onChange={(e) => handleHomepageChange('aboutEyebrow', e.target.value)}
                        placeholder="Who we are"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Title</label>
                      <input
                        type="text"
                        value={homepageContent.aboutTitle}
                        onChange={(e) => handleHomepageChange('aboutTitle', e.target.value)}
                        placeholder="Our story"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Paragraph 1</label>
                      <textarea
                        value={homepageContent.aboutParagraph1}
                        onChange={(e) => handleHomepageChange('aboutParagraph1', e.target.value)}
                        rows="3"
                        placeholder="THE TAG began in 2017..."
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Paragraph 2</label>
                      <textarea
                        value={homepageContent.aboutParagraph2}
                        onChange={(e) => handleHomepageChange('aboutParagraph2', e.target.value)}
                        rows="3"
                        placeholder="Every garment is touched..."
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Button Text</label>
                      <input
                        type="text"
                        value={homepageContent.aboutButton}
                        onChange={(e) => handleHomepageChange('aboutButton', e.target.value)}
                        placeholder="Learn more"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>About Image</label>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                        Upload an image for the About section (max 5MB). Supported formats: JPG, PNG, WebP
                      </p>
                      <div className="media-grid-shopify" style={{ maxWidth: '220px' }}>
                        {homepageContent.aboutImage ? (
                          <div className="media-item-shopify">
                            <div className="media-thumbnail-shopify" style={{ backgroundImage: `url(${homepageContent.aboutImage})` }} />
                            <button 
                              type="button" 
                              className="media-remove-shopify" 
                              onClick={() => handleHomepageChange('aboutImage', '')}
                              disabled={uploadingImages.aboutImage !== undefined}
                            >
                              ×
                            </button>
                            {uploadingImages.aboutImage !== undefined && (
                              <div className="upload-progress-overlay">
                                <div className="upload-progress-bar" style={{ width: `${uploadingImages.aboutImage}%` }}></div>
                                <span className="upload-progress-text">{Math.round(uploadingImages.aboutImage)}%</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="media-item-shopify">
                            <label className="media-upload-label-shopify">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleHomepageImageUpload(file, 'aboutImage');
                                  e.target.value = '';
                                }}
                                style={{ display: 'none' }}
                                disabled={uploadingImages.aboutImage !== undefined}
                              />
                              <div className="media-upload-placeholder">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                  <polyline points="17 8 12 3 7 8"></polyline>
                                  <line x1="12" y1="3" x2="12" y2="15"></line>
                                </svg>
                                <span>Upload About Image</span>
                              </div>
                            </label>
                            {uploadingImages.aboutImage !== undefined && (
                              <div className="upload-progress-overlay">
                                <div className="upload-progress-bar" style={{ width: `${uploadingImages.aboutImage}%` }}></div>
                                <span className="upload-progress-text">{Math.round(uploadingImages.aboutImage)}%</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <input
                        type="url"
                        value={homepageContent.aboutImage || ''}
                        onChange={(e) => handleHomepageChange('aboutImage', e.target.value)}
                        placeholder="Or paste image URL..."
                        style={{ marginTop: '0.75rem' }}
                      />
                    </div>
                      </div>
                    )}
                  </div>

                  {/* Trendy Items Section */}
                  <div className="homepage-section-shopify">
                    <button 
                      className="section-header-toggle-shopify"
                      onClick={() => toggleSection('trendy')}
                    >
                      <h2>Trendy Items Section</h2>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        className={expandedSections.trendy ? 'section-icon-expanded' : ''}
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                    {expandedSections.trendy && (
                      <div className="section-content-shopify">
                        <div className="form-group-shopify">
                      <label>Section Title</label>
                      <input
                        type="text"
                        value={homepageContent.trendyTitle}
                        onChange={(e) => handleHomepageChange('trendyTitle', e.target.value)}
                        placeholder="TRENDY ITEMS"
                      />
                    </div>
                    <div className="form-group-shopify">
                      <label>Section Subtitle</label>
                      <input
                        type="text"
                        value={homepageContent.trendySubtitle}
                        onChange={(e) => handleHomepageChange('trendySubtitle', e.target.value)}
                        placeholder="DISCOVER OUR LATEST COLLECTION..."
                      />
                    </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'categories' && (
            <div>
              <CategoryManager />
              <div style={{ marginTop: '3rem' }}>
                <SubcategoryManager compact onChange={setSubcategories} />
              </div>
            </div>
          )}

          {activeSection === 'orders' && (
            <div className="admin-orders-shopify">
              <div className="page-header-shopify">
                <div>
                  <h1>Orders</h1>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    {orders.filter(o => o.paymentStatus === 'paid').length} paid order{orders.filter(o => o.paymentStatus === 'paid').length !== 1 ? 's' : ''} 
                    {orders.filter(o => o.paymentStatus !== 'paid').length > 0 && (
                      <span style={{ color: '#6b7280' }}>
                        {' • '}
                        {orders.filter(o => o.paymentStatus !== 'paid').length} pending
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <button
                    className="btn-secondary-shopify"
                    onClick={async () => {
                      try {
                        const orderId = await getNextOrderNumber();
            const testOrder = {
              orderId: orderId,
              status: 'paid',
              paymentStatus: 'paid',
                          customer: {
                            firstName: 'Test',
                            lastName: 'Customer',
                            email: 'test@example.com',
                            phone: '+97412345678',
                          },
                          shipping: {
                            street: '123 Test Street',
                            city: 'Doha',
                            state: 'QA',
                            country: 'QA',
                            postalCode: '12345',
                          },
                          items: [
                            {
                              id: 'test-item-1',
                              name: 'Test Abaya',
                              size: 'M',
                              quantity: 1,
                              price: 250.00,
                              image: 'https://via.placeholder.com/300',
                            },
                          ],
                          totals: {
                            subtotal: 250.00,
                            shipping: 0,
                            total: 250.00,
                          },
                          createdAt: serverTimestamp(),
                          paymentMethod: 'skipcash',
                        };

                        console.log('📦 Creating test order:', orderId);
                        const docRef = await addDoc(collection(db, 'orders'), testOrder);
                        console.log('✅ Test order created with document ID:', docRef.id);
                        setFeedback({ type: 'success', message: `Test order ${orderId} created! Check your email (and spam folder) for notification.` });
                        setTimeout(() => setFeedback(null), 5000);
                      } catch (error) {
                        console.error('❌ Error creating test order:', error);
                        setFeedback({ type: 'error', message: 'Failed to create test order. Check console for details.' });
                        setTimeout(() => setFeedback(null), 5000);
                      }
                    }}
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                  >
                    📱 Test Push Notification
                  </button>
                  <button
                    className="btn-secondary-shopify"
                    onClick={async () => {
                      try {
                        const response = await fetch('https://us-central1-abaya-12404.cloudfunctions.net/testPush');
                        const data = await response.json();
                        if (data.success) {
                          setFeedback({ type: 'success', message: '✅ Test push sent! Check your Simplepush app' });
                        } else {
                          setFeedback({ type: 'error', message: `❌ Push failed: ${data.message || 'Unknown error'}` });
                        }
                        setTimeout(() => setFeedback(null), 5000);
                      } catch (error) {
                        console.error('Error testing push:', error);
                        setFeedback({ type: 'error', message: 'Failed to test push. Check console.' });
                        setTimeout(() => setFeedback(null), 5000);
                      }
                    }}
                    style={{ fontSize: '0.875rem', padding: '0.5rem 1rem', marginLeft: '0.5rem' }}
                  >
                    🧪 Direct Push Test
                  </button>
                  <button
                    className="btn-secondary-shopify"
                    onClick={() => {
                      if (orders.length === 0) {
                        setFeedback({ type: 'info', message: 'No orders to delete.' });
                        setTimeout(() => setFeedback(null), 3000);
                        return;
                      }
                      setModal({
                        isOpen: true,
                        title: 'Delete All Orders',
                        message: `Are you sure you want to delete ALL ${orders.length} order${orders.length !== 1 ? 's' : ''}? This action cannot be undone.`,
                        type: 'error',
                        showCancel: true,
                        confirmText: 'Delete All',
                        cancelText: 'Cancel',
                        onConfirm: async () => {
                          try {
                            setIsLoadingOrders(true);
                            const ordersRef = collection(db, 'orders');
                            const deletePromises = orders.map(order => deleteDoc(doc(db, 'orders', order.id)));
                            await Promise.all(deletePromises);
                            setFeedback({ type: 'success', message: `✅ Successfully deleted ${orders.length} order${orders.length !== 1 ? 's' : ''}` });
                            setTimeout(() => setFeedback(null), 5000);
                            setModal({ ...modal, isOpen: false });
                          } catch (error) {
                            console.error('Error deleting orders:', error);
                            setFeedback({ type: 'error', message: 'Failed to delete orders. Check console for details.' });
                            setTimeout(() => setFeedback(null), 5000);
                            setModal({ ...modal, isOpen: false });
                          } finally {
                            setIsLoadingOrders(false);
                          }
                        },
                      });
                    }}
                    style={{ 
                      fontSize: '0.875rem', 
                      padding: '0.5rem 1rem', 
                      marginLeft: '0.5rem',
                      background: '#dc2626',
                      color: '#ffffff',
                      border: '1px solid #dc2626'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#b91c1c';
                      e.target.style.borderColor = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#dc2626';
                      e.target.style.borderColor = '#dc2626';
                    }}
                  >
                    🗑️ Delete All Orders
                  </button>
                </div>
              </div>

              {isLoadingOrders ? (
                <p className="admin-placeholder">Loading orders<span className="loading-dots"></span></p>
              ) : orders.length === 0 ? (
                <p>No orders yet.</p>
              ) : (
                <div className="orders-list-shopify">
                  {orders.map(order => {
                    const isExpanded = expandedOrders.has(order.id);
                    return (
                    <div key={order.id} className="order-card-shopify" data-order-id={order.id} style={{ position: 'relative' }}>
                      <div 
                        className="order-header-shopify order-header-clickable"
                        onClick={() => {
                          setExpandedOrders(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(order.id)) {
                              newSet.delete(order.id);
                            } else {
                              newSet.add(order.id);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div>
                          <h3 className="order-id-shopify">{order.orderId}</h3>
                          <p className="order-date-shopify">
                            {order.createdAt?.toDate ? 
                              new Date(order.createdAt.toDate()).toLocaleString() : 
                              'Date not available'}
                          </p>
                          {!isExpanded && (
                            <p className="order-summary-shopify">
                              {order.customer?.firstName} {order.customer?.lastName} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''} • {order.totals?.total?.toFixed(2)} ر.ق
                            </p>
                          )}
                        </div>
                        <div className="order-status-shopify" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span className={`status-badge-shopify status-${order.status}`}>
                            {order.status || 'pending'}
                          </span>
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ 
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                              cursor: 'pointer'
                            }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="order-expanded-content-shopify">

                      <div className="order-customer-shopify">
                        <div>
                          <strong>{order.customer?.firstName} {order.customer?.lastName}</strong>
                          <p>{order.customer?.email}</p>
                          <p>{order.customer?.phone}</p>
                        </div>
                        <div>
                          <strong>Shipping Address:</strong>
                          <p>
                            {order.shipping?.buildingNumber && `Building ${order.shipping.buildingNumber}`}
                            {order.shipping?.buildingNumber && order.shipping?.streetNumber && ', '}
                            {order.shipping?.streetNumber && `Street ${order.shipping.streetNumber}`}
                            {order.shipping?.zoneNumber && `, Zone ${order.shipping.zoneNumber}`}
                            {order.shipping?.city && `, ${order.shipping.city}`}
                            {order.shipping?.state && `, ${order.shipping.state}`}
                            {order.shipping?.postalCode && ` ${order.shipping.postalCode}`}
                            {/* Fallback for old address format */}
                            {!order.shipping?.buildingNumber && !order.shipping?.streetNumber && order.shipping?.address && `${order.shipping.address}, ${order.shipping.city}`}
                          </p>
                          <p>{order.shipping?.country || 'Qatar'}</p>
                        </div>
                      </div>

                      <div className="order-items-shopify">
                        <h4>Items:</h4>
                        <div className="order-items-grid-shopify">
                          {order.items?.map((item, index) => (
                            <div key={index} className="order-item-shopify">
                              <div 
                                className="order-item-image-shopify"
                                style={{ backgroundImage: `url(${item.image})` }}
                              />
                              <div className="order-item-details-shopify">
                                <p className="order-item-name-shopify">{item.name}</p>
                                <p className="order-item-specs-shopify">
                                  Size: {item.size || 'N/A'} | Length: {item.length || 'N/A'} | Color: {item.color || 'N/A'} | Qty: {item.quantity}
                                </p>
                                {item.notes && (
                                  <p className="order-item-notes-shopify">
                                    Notes: {item.notes}
                                  </p>
                                )}
                                <p className="order-item-price-shopify">
                                  {(item.price * item.quantity).toFixed(2)} ر.ق
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="order-totals-shopify">
                        <div className="order-total-row-shopify">
                          <span>Subtotal:</span>
                          <span>{order.totals?.subtotal?.toFixed(2)} ر.ق</span>
                        </div>
                        <div className="order-total-row-shopify">
                          <span>Shipping:</span>
                          <span>{order.totals?.shipping?.toFixed(2)} ر.ق</span>
                        </div>
                        <div className="order-total-row-shopify order-total-final-shopify">
                          <span>Total:</span>
                          <span>{order.totals?.total?.toFixed(2)} ر.ق</span>
                        </div>
                        <div className="order-payment-method-shopify">
                          Payment: <strong>
                            {order.paymentMethod === 'cash' ? 'Cash on Delivery' : 
                             order.paymentMethod === 'card' ? 'Card Payment' : 
                             order.paymentMethod === 'manual_link' ? 'Admin will send link manually' :
                             order.paymentMethod || 'N/A'}
                          </strong>
                        </div>
                      </div>

                      <div className="order-actions-shopify">
                        <div className="order-actions-left-shopify">
                          <button
                            className="btn-secondary-shopify"
                            onClick={() => openWhatsApp(order)}
                            style={{ 
                              marginRight: '0.5rem',
                              background: '#25D366',
                              color: '#ffffff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            WhatsApp
                          </button>
                          <button
                            className="btn-secondary-shopify"
                            onClick={() => generateReceipt(order)}
                            style={{ marginRight: '0.5rem' }}
                          >
                            Generate Receipt
                          </button>
                          {order.paymentStatus !== 'paid' && (
                            <button
                              className="btn-primary-shopify"
                              onClick={async () => {
                                try {
                                  const orderRef = doc(db, 'orders', order.id);
                                  await updateDoc(orderRef, {
                                    status: 'paid',
                                    updatedAt: serverTimestamp(),
                                  });
                                  setFeedback({ type: 'success', message: 'Order marked as paid!' });
                                } catch (error) {
                                  console.error('Failed to update order status', error);
                                  setFeedback({ type: 'error', message: 'Failed to update order status.' });
                                }
                              }}
                            >
                              Mark as Paid
                            </button>
                          )}
                          {order.status === 'paid' && (
                            <span className="status-badge-shopify status-paid" style={{ 
                              background: '#10b981', 
                              color: '#ffffff',
                              padding: '0.5rem 1rem',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontWeight: '600'
                            }}>
                              ✓ Paid
                            </span>
                          )}
                          {order.paymentStatus === 'paid' && (
                            <button
                              className="btn-cancel-shopify"
                              onClick={() => {
                                setModal({
                                  isOpen: true,
                                  type: 'warning',
                                  title: 'Cancel Order',
                                  message: `Are you sure you want to cancel order ${order.orderId}? This action cannot be undone.`,
                                  confirmText: 'Yes, Cancel Order',
                                  cancelText: 'No, Keep Order',
                                  showCancel: true,
                                  onConfirm: async () => {
                                    try {
                                      const orderRef = doc(db, 'orders', order.id);
                                      await updateDoc(orderRef, {
                                        status: 'cancelled',
                                        updatedAt: serverTimestamp(),
                                      });
                                      setFeedback({ type: 'success', message: 'Order cancelled successfully!' });
                                      setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                    } catch (error) {
                                      console.error('Failed to cancel order', error);
                                      setFeedback({ type: 'error', message: 'Failed to cancel order.' });
                                      setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                    }
                                  },
                                  onCancel: () => setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null })
                                });
                              }}
                            >
                              Cancel Order
                            </button>
                          )}
                        </div>
                        <div className="order-actions-right-shopify">
                          <button
                            className="btn-delete-shopify"
                            onClick={() => {
                              setModal({
                                isOpen: true,
                                type: 'error',
                                title: 'Delete Order',
                                message: `Are you sure you want to permanently delete order ${order.orderId}? This action cannot be undone and all order data will be lost.`,
                                confirmText: 'Yes, Delete',
                                cancelText: 'Cancel',
                                showCancel: true,
                                onConfirm: async () => {
                                  try {
                                    const orderRef = doc(db, 'orders', order.id);
                                    await deleteDoc(orderRef);
                                    setFeedback({ type: 'success', message: 'Order deleted successfully!' });
                                    setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                  } catch (error) {
                                    console.error('Failed to delete order', error);
                                    setFeedback({ type: 'error', message: 'Failed to delete order.' });
                                    setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                  }
                                },
                                onCancel: () => setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null })
                              });
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                        </div>
                      )}
                    </div>
                  )})}
                </div>
              )}
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="admin-settings-shopify">
              <div className="page-header-shopify">
                <div>
                  <h1>Settings</h1>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                    Configure your WhatsApp message templates and other settings
                  </p>
                </div>
              </div>

              {isLoadingSettings ? (
                <p className="admin-placeholder">Loading settings<span className="loading-dots"></span></p>
              ) : (
                <div className="settings-content-shopify" style={{ maxWidth: '800px', marginTop: '2rem' }}>
                  {feedback && (
                    <div 
                      className={`feedback ${feedback.type}`} 
                      style={{
                        marginBottom: '1.5rem',
                        padding: '1rem 1.25rem',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        animation: 'slideIn 0.3s ease-out',
                        backgroundColor: feedback.type === 'success' ? '#d1fae5' : '#fee2e2',
                        color: feedback.type === 'success' ? '#065f46' : '#991b1b',
                        border: `1px solid ${feedback.type === 'success' ? '#10b981' : '#ef4444'}`
                      }}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                      >
                        {feedback.type === 'success' ? (
                          <>
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                          </>
                        ) : (
                          <>
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </>
                        )}
                      </svg>
                      <span>{feedback.message}</span>
                    </div>
                  )}
                  {/* WhatsApp Message Template - Moved to top */}
                  <div className="form-section-shopify">
                    <label className="section-label-shopify">WhatsApp Message Template</label>
                    <div className="form-group-shopify">
                      <label style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.875rem', fontWeight: '500' }}>
                        Message Template
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6b7280', fontWeight: 'normal' }}>
                          (Use placeholders: {'{customerName}'}, {'{orderId}'}, {'{total}'}, {'{itemCount}'}, {'{items}'})
                        </span>
                      </label>
                      <textarea
                        value={settings.whatsappMessage || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, whatsappMessage: e.target.value }))}
                        rows={10}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                        placeholder="Enter your WhatsApp message template..."
                      />
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '4px' }}>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', fontWeight: '600' }}>Available Placeholders:</p>
                        <ul style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0, paddingLeft: '1.5rem' }}>
                          <li><code>{'{customerName}'}</code> - Customer's full name</li>
                          <li><code>{'{orderId}'}</code> - Order ID</li>
                          <li><code>{'{total}'}</code> - Order total amount</li>
                          <li><code>{'{itemCount}'}</code> - Number of items</li>
                          <li><code>{'{items}'}</code> - List of items</li>
                        </ul>
                      </div>
                    </div>
                    <div style={{ marginTop: '1.5rem' }}>
                      <button
                        className="btn-primary-shopify"
                        onClick={() => saveSettings(settings)}
                      >
                        Save Settings
                      </button>
                    </div>
                  </div>

                  <div className="form-section-shopify" style={{ marginTop: '3rem' }}>
                    <SubcategoryManager compact onChange={setSubcategories} />
                  </div>

                  <div className="form-section-shopify" style={{ marginTop: '3rem' }}>
                    <label className="section-label-shopify">Contact Information</label>
                    <div className="form-group-shopify">
                      <label style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.875rem', fontWeight: '500' }}>
                        Phone Number
                      </label>
                      <input
                        type="text"
                        value={settings.contactPhone || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, contactPhone: e.target.value }))}
                        placeholder="+974 1234 5678"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    <div className="form-group-shopify" style={{ marginTop: '1rem' }}>
                      <label style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.875rem', fontWeight: '500' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={settings.contactEmail || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, contactEmail: e.target.value }))}
                        placeholder="info@thetag.com"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    <div className="form-group-shopify" style={{ marginTop: '1rem' }}>
                      <label style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.875rem', fontWeight: '500' }}>
                        Location
                      </label>
                      <input
                        type="text"
                        value={settings.contactLocation || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, contactLocation: e.target.value }))}
                        placeholder="Doha, Qatar"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                    <div className="form-group-shopify" style={{ marginTop: '1rem' }}>
                      <label style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.875rem', fontWeight: '500' }}>
                        Instagram URL
                      </label>
                      <input
                        type="url"
                        value={settings.instagramUrl || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, instagramUrl: e.target.value }))}
                        placeholder="https://www.instagram.com/yourusername"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit'
                        }}
                      />
                      <small style={{ marginTop: '0.5rem', display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                        Your Instagram profile URL (e.g., https://www.instagram.com/yourusername)
                      </small>
                    </div>

                    {/* Shipping Methods Management */}
                    <div className="form-group-shopify" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                      <label style={{ marginBottom: '1rem', display: 'block', fontSize: '1rem', fontWeight: '600' }}>
                        Shipping Methods
                      </label>
                      <small style={{ marginBottom: '1rem', display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                        Manage shipping methods and their corresponding delivery fees. Customers will select from these options at checkout.
                      </small>

                      {/* Add New Shipping Method */}
                      <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f9fafb', borderRadius: '4px' }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem' }}>Add New Shipping Method</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <input
                            type="text"
                            placeholder="Area name (e.g., Inside Doha)"
                            value={newShippingMethod.name}
                            onChange={(e) => setNewShippingMethod({ ...newShippingMethod, name: e.target.value })}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <div className="input-with-currency" style={{ flex: 1 }}>
                              <span className="currency-symbol">ر.ق</span>
                              <input
                                type="number"
                                placeholder="Delivery fee"
                                value={newShippingMethod.fee}
                                onChange={(e) => setNewShippingMethod({ ...newShippingMethod, fee: e.target.value })}
                                min="0"
                                step="0.01"
                                style={{
                                  width: '100%',
                                  padding: '0.75rem',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '0.875rem',
                                  fontFamily: 'inherit',
                                  outline: 'none'
                                }}
                              />
                            </div>
                            <input
                              type="text"
                              placeholder="Description (optional)"
                              value={newShippingMethod.description}
                              onChange={(e) => setNewShippingMethod({ ...newShippingMethod, description: e.target.value })}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn-secondary-shopify"
                            onClick={async () => {
                              if (!newShippingMethod.name || !newShippingMethod.fee) {
                                setFeedback({ type: 'error', message: 'Please fill in area name and delivery fee' });
                                setTimeout(() => setFeedback(null), 3000);
                                return;
                              }
                              try {
                                const maxOrder = shippingMethods.length > 0 
                                  ? Math.max(...shippingMethods.map(m => m.order || 0))
                                  : 0;
                                await addDoc(collection(db, 'shippingMethods'), {
                                  name: newShippingMethod.name,
                                  fee: parseFloat(newShippingMethod.fee),
                                  description: newShippingMethod.description || '',
                                  order: maxOrder + 1,
                                  active: true,
                                  createdAt: serverTimestamp(),
                                });
                                setNewShippingMethod({ name: '', fee: '', description: '' });
                                fetchShippingMethods();
                                setFeedback({ type: 'success', message: 'Shipping method added successfully!' });
                                setTimeout(() => setFeedback(null), 3000);
                              } catch (error) {
                                console.error('Error adding shipping method:', error);
                                setFeedback({ type: 'error', message: 'Failed to add shipping method' });
                                setTimeout(() => setFeedback(null), 3000);
                              }
                            }}
                            style={{ alignSelf: 'flex-start' }}
                          >
                            Add Shipping Method
                          </button>
                        </div>
                      </div>

                      {/* List of Shipping Methods */}
                      {isLoadingShippingMethods ? (
                        <p className="admin-placeholder">Loading shipping methods<span className="loading-dots"></span></p>
                      ) : shippingMethods.length === 0 ? (
                        <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>No shipping methods yet. Add one above.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {shippingMethods.map((method) => (
                            <div
                              key={method.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '0.75rem',
                                background: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '4px',
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{method.name}</div>
                                {method.description && (
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                    {method.description}
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '500', minWidth: '80px', textAlign: 'right' }}>
                                {method.fee?.toFixed(2)} ر.ق
                              </div>
                              <button
                                type="button"
                                className="btn-delete-shopify"
                                onClick={async () => {
                                  setModal({
                                    isOpen: true,
                                    type: 'error',
                                    title: 'Delete Shipping Method',
                                    message: `Are you sure you want to delete "${method.name}"?`,
                                    confirmText: 'Yes, Delete',
                                    cancelText: 'Cancel',
                                    showCancel: true,
                                    onConfirm: async () => {
                                      try {
                                        await deleteDoc(doc(db, 'shippingMethods', method.id));
                                        fetchShippingMethods();
                                        setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                        setFeedback({ type: 'success', message: 'Shipping method deleted successfully!' });
                                        setTimeout(() => setFeedback(null), 3000);
                                      } catch (error) {
                                        console.error('Error deleting shipping method:', error);
                                        setFeedback({ type: 'error', message: 'Failed to delete shipping method' });
                                        setTimeout(() => setFeedback(null), 3000);
                                        setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                      }
                                    },
                                    onCancel: () => setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null })
                                  });
                                }}
                                style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'reports' && (
            <div className="admin-reports-shopify">
              <div className="page-header-shopify">
                <h1>Sales Report</h1>
              </div>

              <div className="reports-stats-shopify">
                <div className="report-card-shopify">
                  <div className="report-label-shopify">Total Revenue</div>
                  <div className="report-value-shopify">
                    {totalRevenue.toFixed(2)} ر.ق
                  </div>
                  <div className="report-subtitle-shopify">All time sales</div>
                </div>

                <div className="report-card-shopify">
                  <div className="report-label-shopify">Paid Orders</div>
                  <div className="report-value-shopify">{paidOrders.length}</div>
                  <div className="report-subtitle-shopify">Completed payments</div>
                </div>

                <div className="report-card-shopify">
                  <div className="report-label-shopify">Units Sold</div>
                  <div className="report-value-shopify">{totalUnitsSold}</div>
                  <div className="report-subtitle-shopify">Items sold</div>
                </div>

                <div className="report-card-shopify">
                  <div className="report-label-shopify">Total Products</div>
                  <div className="report-value-shopify">{totalProducts}</div>
                  <div className="report-subtitle-shopify">Live products</div>
                </div>

                <div className="report-card-shopify">
                  <div className="report-label-shopify">Featured Items</div>
                  <div className="report-value-shopify">{featuredCount}</div>
                  <div className="report-subtitle-shopify">On homepage</div>
                </div>

                <div className="report-card-shopify">
                  <div className="report-label-shopify">Average Order Value</div>
                  <div className="report-value-shopify">
                    {averageOrderValue.toFixed(2)} ر.ق
                  </div>
                  <div className="report-subtitle-shopify">Paid orders only</div>
                </div>
              </div>

              <div className="reports-section-shopify">
                <h2>Product Performance</h2>
                {isLoading ? (
                  <p className="admin-placeholder">Loading<span className="loading-dots"></span></p>
                ) : products.length === 0 ? (
                  <p>No products yet.</p>
                ) : (
                  <div className="reports-table-shopify">
                    <div className="reports-table-header-shopify">
                      <div className="report-col-name">Product</div>
                      <div className="report-col-price">Price</div>
                      <div className="report-col-category">Category</div>
                      <div className="report-col-units">Units Sold</div>
                      <div className="report-col-revenue">Revenue</div>
                      <div className="report-col-views">Views (30d)</div>
                      <div className="report-col-conversion">Conversion</div>
                    </div>
                    {products.map(product => (
                      <div key={product.id} className="reports-table-row-shopify">
                        <div className="report-col-name">
                          <div className="report-product-image-shopify" style={{ backgroundImage: `url(${product.image})` }} />
                          <span>{product.name}</span>
                        </div>
                        <div className="report-col-price">{product.price?.toFixed(2) || product.price} ر.ق</div>
                        <div className="report-col-category">{product.category}</div>
                        <div className="report-col-units">
                          {salesByProduct.get(product.id)?.units || 0}
                        </div>
                        <div className="report-col-revenue">
                          {(salesByProduct.get(product.id)?.revenue || 0).toFixed(2)} ر.ق
                        </div>
                        <div className="report-col-views">
                          {isLoadingProductViews ? '—' : (productViewCounts[product.id] || 0)}
                        </div>
                        <div className="report-col-conversion">
                          {(() => {
                            const views = productViewCounts[product.id] || 0;
                            const units = salesByProduct.get(product.id)?.units || 0;
                            if (isLoadingProductViews || views === 0) return '—';
                            const conversion = (units / views) * 100;
                            return `${conversion.toFixed(1)}%`;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'products' && (
            <div className="admin-products-shopify">
              <div className="page-header-shopify">
                <h1>Products</h1>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedProducts.size > 0 && (
                    <button 
                      className="btn-secondary-shopify" 
                      onClick={() => setIsBulkEditOpen(true)}
                      style={{ background: '#000000', color: '#ffffff' }}
                    >
                      Bulk Edit ({selectedProducts.size})
                    </button>
                  )}
                  <button 
                    className="btn-secondary-shopify" 
                    onClick={() => setSelectedProducts(new Set())}
                    disabled={selectedProducts.size === 0}
                  >
                    Clear Selection
                  </button>
                  <button className="btn-primary-shopify" onClick={() => {
                    setFormData(getInitialForm());
                    setHasUnsavedChanges(false);
                    setActiveSection('add');
                  }}>
                    Add Product
                  </button>
                </div>
              </div>

              <div className="products-filters-shopify">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <select
                  className="products-filter-select"
                  value={productCategoryFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProductCategoryFilter(value);
                    if (!value || (productSubcategoryFilter && !filterSubcategories.some(s => s.id === productSubcategoryFilter))) {
                      setProductSubcategoryFilter('');
                    }
                  }}
                >
                  <option value="">All categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.label}
                    </option>
                  ))}
                </select>
                <select
                  className="products-filter-select"
                  value={productSubcategoryFilter}
                  onChange={(e) => setProductSubcategoryFilter(e.target.value)}
                  disabled={!productCategoryFilter}
                >
                  <option value="">All subcategories</option>
                  {filterSubcategories.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.label}
                    </option>
                  ))}
                </select>
                {(searchQuery || productCategoryFilter || productSubcategoryFilter) && (
                  <button
                    className="btn-secondary-shopify"
                    onClick={() => {
                      setSearchQuery('');
                      setProductCategoryFilter('');
                      setProductSubcategoryFilter('');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
              
              {feedback && (
                <div className={`feedback ${feedback.type}`} style={{ marginBottom: '1rem' }}>
                  {feedback.message}
                </div>
              )}
              {isLoading ? (
                <p className="admin-placeholder">Loading<span className="loading-dots"></span></p>
              ) : filteredProducts.length === 0 ? (
                <p>No products yet.</p>
              ) : (
                <div className="products-table-shopify">
                  {filteredProducts.map(product => {
                    const isSelected = selectedProducts.has(product.id);
                    return (
                      <div key={product.id} className={`product-row-shopify ${isSelected ? 'selected' : ''}`} style={{ position: 'relative' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const newSelected = new Set(selectedProducts);
                            if (e.target.checked) {
                              newSelected.add(product.id);
                            } else {
                              newSelected.delete(product.id);
                            }
                            setSelectedProducts(newSelected);
                          }}
                          style={{
                            position: 'absolute',
                            left: '1rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                            zIndex: 1
                          }}
                        />
                        <div className="product-image-shopify" style={{ backgroundImage: `url(${product.image || product.images?.[0]})`, marginLeft: isSelected ? '2.5rem' : '0' }} />
                        <div className="product-info-shopify">
                          <div className="product-name-shopify">{product.name}</div>
                          <div className="product-meta-shopify">
                            {categories.find(c => c.id === product.category)?.label || product.category || 'Uncategorized'}
                            {product.subcategory ? (
                              <> • {subcategories.find(s => s.id === product.subcategory)?.label || product.subcategory}</>
                            ) : null}
                          </div>
                          <div className="product-price-shopify">{product.price?.toFixed(2) || product.price} ر.ق</div>
                        </div>
                        <div className="product-actions-shopify" style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                          <button
                            className="btn-secondary-shopify"
                            onClick={() => {
                              // Convert images to array format for editing
                              const productImages = product.images || [];
                              if (product.image && !productImages.includes(product.image)) {
                                productImages.unshift(product.image);
                              }
                              // Add image2, image3, image4 if they exist
                              [product.image2, product.image3, product.image4].forEach(img => {
                                if (img && !productImages.includes(img)) {
                                  productImages.push(img);
                                }
                              });
                              
                              // Load product data into form for editing
                              setFormData({
                                name: product.name || '',
                                price: product.price || '',
                                badge: product.badge || '',
                                category: product.category || '',
                                subcategory: product.subcategory || '',
                                images: productImages,
                                image: product.image || productImages[0] || '', // Keep for backward compatibility
                                image2: productImages[1] || '',
                                image3: productImages[2] || '',
                                image4: productImages[3] || '',
                                description: product.description || '',
                                selectedSizes: product.sizes || [],
                                availability: product.availability || 'In Stock',
                                status: product.status || 'draft',
                                featured: product.featured || false,
                                editingProductId: product.id, // Store ID for update
                              });
                              setHasUnsavedChanges(false);
                              setActiveSection('add');
                              // Scroll to top
                              window.scrollTo(0, 0);
                            }}
                            style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                          >
                            Edit
                          </button>
                        <button
                          className="btn-delete-shopify"
                          onClick={() => {
                            setModal({
                              isOpen: true,
                              type: 'error',
                              title: 'Delete Product',
                              message: `Are you sure you want to permanently delete "${product.name}"? This action cannot be undone.`,
                              confirmText: 'Yes, Delete',
                              cancelText: 'Cancel',
                              showCancel: true,
                              onConfirm: async () => {
                                try {
                                  const productRef = doc(db, 'products', product.id);
                                  await deleteDoc(productRef);
                                  setFeedback({ type: 'success', message: 'Product deleted successfully!' });
                                  setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                  fetchProducts();
                                } catch (error) {
                                  console.error('Failed to delete product', error);
                                  setFeedback({ type: 'error', message: 'Failed to delete product.' });
                                  setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
                                }
                              },
                              onCancel: () => setModal({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null })
                            });
                          }}
                          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                        >
                          Delete
                        </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeSection === 'add' && (
            <div className="admin-add-product-shopify">
              <div className="page-header-shopify" style={{ marginBottom: '2rem' }}>
                <h1>{formData.editingProductId ? 'Edit Product' : 'Add Product'}</h1>
              </div>
              <form onSubmit={handleSubmit} className="product-form-shopify">
                {/* Product Title */}
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Product name"
                  className="product-title-input"
                  required
                />

                {/* Description */}
                <div className="form-section-shopify">
                  <label className="section-label-shopify">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="8"
                    placeholder="Describe your product..."
                    className="description-textarea"
                    required
                  />
                </div>

                {/* Media */}
                <div className="form-section-shopify">
                  <label className="section-label-shopify">Media</label>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                    Upload product images (max 4 images, 5MB each). Drag to reorder images.
                  </p>
                  <div className="media-grid-shopify" style={{ position: 'relative' }}>
                    {(() => {
                      const allImages = [
                        formData.image,
                        formData.image2,
                        formData.image3,
                        formData.image4
                      ].filter(img => img);
                      
                      return allImages.map((img, index) => (
                        <div
                          key={`${img}-${index}`}
                          className="media-item-shopify"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/html', index.toString());
                            e.currentTarget.style.opacity = '0.5';
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const dragIndex = parseInt(e.dataTransfer.getData('text/html'), 10);
                            if (dragIndex !== index) {
                              handleImageReorder(dragIndex, index);
                            }
                            e.currentTarget.style.opacity = '1';
                          }}
                          style={{ cursor: 'move', position: 'relative' }}
                        >
                          <div className="media-thumbnail-shopify" style={{ backgroundImage: `url(${img})` }} />
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            left: '0.5rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {index + 1}
                          </div>
                          <button 
                            type="button" 
                            className="media-remove-shopify" 
                            onClick={() => {
                              const imageKey = index === 0 ? 'image' : `image${index + 1}`;
                              setFormData(prev => ({ ...prev, [imageKey]: '' }));
                              setHasUnsavedChanges(true);
                            }}
                            disabled={uploadingImages[`image${index === 0 ? '' : index + 1}`] !== undefined}
                          >
                            ×
                          </button>
                          {uploadingImages[`image${index === 0 ? '' : index + 1}`] !== undefined && (
                            <div className="upload-progress-overlay">
                              <div className="upload-progress-bar" style={{ width: `${uploadingImages[`image${index === 0 ? '' : index + 1}`]}%` }}></div>
                              <span className="upload-progress-text">{Math.round(uploadingImages[`image${index === 0 ? '' : index + 1}`])}%</span>
                            </div>
                          )}
                        </div>
                      ));
                    })()}
                    {/* Upload slots for images */}
                    {!formData.image && (
                      <div className="media-item-shopify">
                        <label className="media-upload-label-shopify">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'image');
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                            disabled={uploadingImages.image !== undefined}
                          />
                          <div className="media-upload-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            <span>Upload Image 1</span>
                          </div>
                        </label>
                        {uploadingImages.image !== undefined && (
                          <div className="upload-progress-overlay">
                            <div className="upload-progress-bar" style={{ width: `${uploadingImages.image}%` }}></div>
                            <span className="upload-progress-text">{Math.round(uploadingImages.image)}%</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!formData.image2 && (
                      <div className="media-item-shopify">
                        <label className="media-upload-label-shopify">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'image2');
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                            disabled={uploadingImages.image2 !== undefined}
                          />
                          <div className="media-upload-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            <span>Upload Image 2</span>
                          </div>
                        </label>
                        {uploadingImages.image2 !== undefined && (
                          <div className="upload-progress-overlay">
                            <div className="upload-progress-bar" style={{ width: `${uploadingImages.image2}%` }}></div>
                            <span className="upload-progress-text">{Math.round(uploadingImages.image2)}%</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!formData.image3 && (
                      <div className="media-item-shopify">
                        <label className="media-upload-label-shopify">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'image3');
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                            disabled={uploadingImages.image3 !== undefined}
                          />
                          <div className="media-upload-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            <span>Upload Image 3</span>
                          </div>
                        </label>
                        {uploadingImages.image3 !== undefined && (
                          <div className="upload-progress-overlay">
                            <div className="upload-progress-bar" style={{ width: `${uploadingImages.image3}%` }}></div>
                            <span className="upload-progress-text">{Math.round(uploadingImages.image3)}%</span>
                          </div>
                        )}
                      </div>
                    )}
                    {!formData.image4 && (
                      <div className="media-item-shopify">
                        <label className="media-upload-label-shopify">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleImageUpload(file, 'image4');
                              e.target.value = '';
                            }}
                            style={{ display: 'none' }}
                            disabled={uploadingImages.image4 !== undefined}
                          />
                          <div className="media-upload-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            <span>Upload Image 4</span>
                          </div>
                        </label>
                        {uploadingImages.image4 !== undefined && (
                          <div className="upload-progress-overlay">
                            <div className="upload-progress-bar" style={{ width: `${uploadingImages.image4}%` }}></div>
                            <span className="upload-progress-text">{Math.round(uploadingImages.image4)}%</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div className="form-section-shopify">
                  <label className="section-label-shopify">Pricing</label>
                  <div className="form-group-shopify">
                    <label>Price</label>
                    <div className="input-with-currency">
                      <span className="currency-symbol">ر.ق</span>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        step="0.01"
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group-shopify">
                    <label>Category *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Select a category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                    {categories.length === 0 && !isLoadingCategories && (
                      <small style={{ color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>
                        No categories available. <button 
                          type="button" 
                          onClick={() => setActiveSection('categories')}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: '#000000', 
                            textDecoration: 'underline', 
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 'inherit'
                          }}
                        >
                          Add categories here
                        </button>
                      </small>
                    )}
                  </div>
                  <div className="form-group-shopify">
                    <label>Subcategory (optional)</label>
                    <select
                      name="subcategory"
                      value={formData.subcategory}
                      onChange={handleChange}
                      disabled={!formData.category || availableSubcategories.length === 0}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        background: 'white',
                        cursor: !formData.category || availableSubcategories.length === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value="">No subcategory</option>
                      {availableSubcategories.map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.label}
                        </option>
                      ))}
                    </select>
                    {formData.category && availableSubcategories.length === 0 && !isLoadingSubcategories && (
                      <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                        No subcategories for this category yet. Add them in Settings.
                      </small>
                    )}
                  </div>
                  <div className="form-group-shopify">
                    <label>Badge (optional)</label>
                    <select
                      name="badge"
                      value={formData.badge}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                        fontFamily: 'inherit',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">None</option>
                      <option value="NEW">NEW</option>
                      <option value="POPULAR">POPULAR</option>
                      <option value="SALE">SALE</option>
                      <option value="Limited">Limited</option>
                      <option value="Exclusive">Exclusive</option>
                      <option value="Best Seller">Best Seller</option>
                    </select>
                  </div>
                </div>

                {/* Product Details */}
                <div className="form-section-shopify">
                  <label className="section-label-shopify">Product Details</label>
                  <div className="form-group-shopify">
                    <label>Availability</label>
                    <select
                      name="availability"
                      value={formData.availability}
                      onChange={handleChange}
                    >
                      <option value="In Stock">In Stock</option>
                      <option value="Few Pieces Left">Few Pieces Left</option>
                      <option value="Out of Stock">Out of Stock</option>
                    </select>
                  </div>
                </div>

                {/* Options */}
                <div className="form-section-shopify">
                  <label className="section-label-shopify">Options</label>
                  <div className="form-group-shopify">
                    <label>Sizes</label>
                    <div className="sizes-grid-shopify">
                      {availableSizes.map(size => (
                        <label key={size} className="size-checkbox-shopify">
                          <input
                            type="checkbox"
                            checked={formData.selectedSizes.includes(size)}
                            onChange={() => handleSizeToggle(size)}
                          />
                          <span>{size}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          )}
        </main>

        {/* Right Sidebar */}
        {activeSection === 'add' && (
          <aside className="admin-rightbar-shopify">
            {/* Status */}
            <div className="rightbar-section-shopify">
              <label className="rightbar-label-shopify">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="status-select-shopify"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
              </select>
            </div>

            {/* Featured */}
            <div className="rightbar-section-shopify">
              <label className="rightbar-label-shopify">Display</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured || false}
                  onChange={(e) => {
                    setFormData({ ...formData, featured: e.target.checked });
                    setHasUnsavedChanges(true);
                  }}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#000000'
                  }}
                />
                <span>Show in Trendy Items</span>
              </label>
              <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
                Featured products appear in the "TRENDY ITEMS" section on homepage
              </small>
            </div>
          </aside>
        )}

        {/* Floating Save Button */}
        {activeSection === 'add' && hasUnsavedChanges && (
          <div className="floating-save-shopify">
            <div className="floating-save-content">
              <span className="unsaved-text">Unsaved changes</span>
              <div className="floating-actions">
                <button type="button" className="btn-discard-floating" onClick={handleDiscard}>
                  Discard
                </button>
                <button type="button" className="btn-save-floating" onClick={handleSubmit} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Edit Modal */}
      {isBulkEditOpen && (
        <div className="modal-overlay" onClick={() => setIsBulkEditOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Bulk Edit Products ({selectedProducts.size})</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="form-group-shopify">
                <label>Category</label>
                <select
                  value={bulkEditData.category}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, category: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group-shopify">
                <label>Subcategory</label>
                <select
                  value={bulkEditData.subcategory}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, subcategory: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  <option value="__clear__">Clear subcategory</option>
                  {subcategories
                    .filter(sub => !bulkEditData.category || sub.categoryId === bulkEditData.category)
                    .map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.label}</option>
                    ))}
                </select>
              </div>
              <div className="form-group-shopify">
                <label>Badge</label>
                <select
                  value={bulkEditData.badge}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, badge: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  <option value="__clear__">Clear badge</option>
                  {badgeOptions.filter(b => b).map(badge => (
                    <option key={badge} value={badge}>{badge}</option>
                  ))}
                </select>
              </div>
              <div className="form-group-shopify">
                <label>Availability</label>
                <select
                  value={bulkEditData.availability}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, availability: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  <option value="In Stock">In Stock</option>
                  <option value="Few Pieces Left">Few Pieces Left</option>
                  <option value="Out of Stock">Out of Stock</option>
                  <option value="Pre-order">Pre-order</option>
                </select>
              </div>
              <div className="form-group-shopify">
                <label>Status</label>
                <select
                  value={bulkEditData.status}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div className="form-group-shopify">
                <label>Featured</label>
                <select
                  value={bulkEditData.featured === null ? '' : bulkEditData.featured ? 'true' : 'false'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBulkEditData(prev => ({ 
                      ...prev, 
                      featured: value === '' ? null : value === 'true' 
                    }));
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                  }}
                >
                  <option value="">Keep current</option>
                  <option value="true">Set as Featured</option>
                  <option value="false">Remove Featured</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary-shopify"
                onClick={() => {
                  setIsBulkEditOpen(false);
                  setBulkEditData({
                    category: '',
                    subcategory: '',
                    badge: '',
                    availability: '',
                    status: '',
                    featured: null,
                  });
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="btn-primary-shopify"
                onClick={handleBulkEdit}
                disabled={isSaving}
              >
                {isSaving ? 'Updating...' : `Update ${selectedProducts.size} Product(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText || 'OK'}
        cancelText={modal.cancelText || 'Cancel'}
        showCancel={modal.showCancel !== undefined ? modal.showCancel : false}
      />

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Modal removed - too intrusive. Users can click on orders to view details */}
    </div>
  );
}

export default AdminPage;
